import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (hamburgerRef.current && hamburgerRef.current.contains(target)) return;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        closeMobileMenu();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [dropdownOpen]);

  const linkClass = `text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
    scrolled ? "text-foreground-700 hover:text-foreground-950" : "text-white/90 hover:text-white"
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-black/25 backdrop-blur-[2px]"
      }`}
    >
      <div className="w-full mx-auto flex items-center justify-between h-16 md:h-18 px-4 md:px-6">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-1.5">
            <span className={`text-lg md:text-xl font-heading font-bold transition-colors duration-300 ${scrolled ? "text-foreground-950" : "text-white"}`}>
              PandaBuy<span className="font-normal italic">.Courses</span>
            </span>
          </Link>
          <Link to="/" className={`hidden lg:inline-flex ${linkClass}`}>
            Home
          </Link>
          <Link to="/pandabuy-sheet-ultimate-guide" className={`hidden lg:inline-flex ${linkClass}`}>
            Shopping Guide
          </Link>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          {/* Desktop categories dropdown */}
          <div className="relative hidden sm:block" ref={dropdownRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(!dropdownOpen);
              }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-background-100 text-foreground-800 hover:bg-background-200"
                  : "bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm"
              }`}
            >
              Categories
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-sm transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm transition-transform duration-200"></i>
              )}
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-56 bg-white rounded-xl shadow-lg border border-background-200 overflow-hidden py-1.5 z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/pandabuy-sheet-ultimate-guide"
            className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium transition-all duration-300 whitespace-nowrap ${
              scrolled ? "text-foreground-600 hover:text-foreground-900" : "text-white/80 hover:text-white"
            }`}
          >
            <i className="ri-book-open-line"></i>
            <span className="hidden md:inline">Guide</span>
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
              scrolled
                ? "bg-primary-500 text-white hover:bg-primary-600"
                : "bg-white text-primary-600 hover:bg-white/90"
            }`}
          >
            pandabuy sheet
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            onClick={(e) => {
              e.stopPropagation();
              setMobileMenuOpen((prev) => !prev);
            }}
            className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? "text-foreground-800 hover:bg-background-100" : "text-white hover:bg-white/15"
            }`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden bg-white border-t border-background-200 shadow-lg transition-all duration-300 origin-top ${
          mobileMenuOpen
            ? "opacity-100 scale-y-100 max-h-[80vh]"
            : "opacity-0 scale-y-0 max-h-0 pointer-events-none"
        }`}
      >
        <div className="px-4 py-3 space-y-1 overflow-y-auto max-h-[80vh]">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
            onClick={closeMobileMenu}
          >
            <i className="ri-home-line"></i>
            Home
          </Link>
          <Link
            to="/pandabuy-sheet-ultimate-guide"
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
            onClick={closeMobileMenu}
          >
            <i className="ri-book-open-line"></i>
            Shopping Guide
          </Link>
          <div className="pt-2 pb-1 px-3">
            <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</span>
          </div>
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
              onClick={closeMobileMenu}
            >
              <span>{cat.name}</span>
              {cat.tag && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  {cat.tag}
                </span>
              )}
            </a>
          ))}
          <div className="pt-3 px-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              onClick={closeMobileMenu}
            >
              pandabuy sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}